<template>
  <div id="concert" class="mk-concert">
    <h1 class="structure-header">
      {{ $t('CONCERT.TITLE') }} | MUZIK AIR
    </h1>
    <div class="container">
      <ConcertTopBanner />
      <ConcertSearchBanner
        @update-params="updateParams"
      />
      <ConcertContent
        :table-data="tableData"
        :is-loaded="isLoaded"
        :is-load-more="isLoadMore"
      />
      <div v-if="(isLoaded && !isLoadMore) && !isLastPage && loadMoreCounter >= 0">
        <LoadMoreBtn :btn-content="$t('CONCERT.MORE')" :on-click="manualLoadMore" />
      </div>
      <div class="pgnnp-footer">
        <Paganiniplus />
      </div>
    </div>
  </div>
</template>

<script>
import ConcertTopBanner from '@/components/concert/ConcertTopBanner'
import ConcertSearchBanner from '@/components/concert/ConcertSearchBanner'
import ConcertContent from '@/components/concert/ConcertContent'
import LoadMoreBtn from '@/components/common/LoadMoreBtn'
import Paganiniplus from '@/components/tsp/Paganiniplus'
import EventService from '../../assets/services/EventService'

export default {
  name: 'Concert',
  components: {
    Paganiniplus,
    ConcertTopBanner,
    ConcertSearchBanner,
    ConcertContent,
    LoadMoreBtn
  },
  props: {},
  data() {
    return {
      tableData: [],
      tableQueryParams: {},
      pagination: {
        current_page: 1,
        last_page: 1
      },
      isLoaded: false,
      triggerInfiniteScrolling: false,
      loadMoreDebounceTimer: 0,
      isLoadMore: false,
      loadMoreCounter: 0
    }
  },
  metaInfo() {
    return {
      title: this.title,
      meta: [
        ...this.$route.meta.metaInfo.meta,
        { vmid: 'name', name: 'name', content: this.title },
        { vmid: 'description', name: 'description', content: this.description },
        { vmid: 'keywords', name: 'keywords', content: this.keywords },
        { vmid: 'og:title', name: 'og:title', content: this.title },
        { vmid: 'og:description', name: 'og:description', content: this.description },
        { vmid: 'og:url', name: 'og:url', content: this.canonicalURL }
      ],
      link: [
        { vmid: 'canonical', rel: 'canonical', href: this.canonicalURL },
        { vmid: 'image_src', rel: 'image_src', href: this.cover }
      ],
      __dangerouslyDisableSanitizers: ['script'],
      script: [{ innerHTML: JSON.stringify(this.structuredData), type: 'application/ld+json' }]
    }
  },
  computed: {
    isLastPage() {
      return this.pagination.last_page <= this.pagination.current_page
    },
    title() {
      return `${this.$t('CONCERT.TITLE')} | MUZIK AIR`
    },
    description() {
      return this.$t('CONCERT.META_DESCRIPTION') || this.title
    },
    keywords() {
      return this.$te('CONCERT.META_KEYWORDS')
        ? this.$t('CONCERT.META_KEYWORDS') || this.$t('CONCERT.TITLE')
        : this.$t('CONCERT.TITLE')
    },
    cover() {
      return `https://${process.env.VUE_APP_MUZIK_CONCERT}.${process.env.VUE_APP_MUZIK_DOMAIN}/img/cover-social/concert.png`
    },
    canonicalURL() {
      return `https://${process.env.VUE_APP_MUZIK_CONCERT}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}`
    },
    structuredData() {
      return [{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        '@id': this.canonicalURL,
        url: this.canonicalURL,
        image: this.cover,
        name: this.title,
        description: this.description,
        author: {
          '@type': 'Organization',
          sameAs: [
            'https://facebook.com/muzikcom/',
            'https://www.instagram.com/muzikmagazine/',
            'http://line.me/ti/p/@pwr7122w#~',
            'https://www.youtube.com/user/MUZIKmagazine'
          ]
        },
        relatedLink: [
          `https://${process.env.VUE_APP_MUZIK_CSS}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}`,
          `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}`,
          `https://${process.env.VUE_APP_MUZIK_READ}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}`,
          `https://${process.env.VUE_APP_MUZIK_CONCERT}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/terms`,
          `https://${process.env.VUE_APP_MUZIK_CONCERT}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/privacy`,
          `https://${process.env.VUE_APP_MUZIK_CONCERT}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/about`,
          `https://${process.env.VUE_APP_MUZIK_CONCERT}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/mediaKit`
        ]
      },
      {
        '@context': 'http://schema.org',
        '@type': 'Organization',
        url: `https://${process.env.VUE_APP_MUZIK_CONCERT}.${process.env.VUE_APP_MUZIK_DOMAIN}`,
        logo: {
          '@type': 'ImageObject',
          'url': this.cover
        }
      }]
    }
  },
  watch: {},
  created() {
  },
  mounted() {
  },
  destroyed() {
    // window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    async getEvents(query, page, perPage) {
      this.isLoaded = false
      let res = await EventService.getEventList(query, page, perPage, 'list')
      if (res && res.data) {
        this.tableData = res.data
        if (res.meta) {
          this.pagination = {
            current_page: res.meta.current_page,
            last_page: res.meta.last_page,
            per_page: res.meta.per_page
          }
        }
      } else {
        this.tableData = null
      }
      this.isLoaded = true
    },
    async updateParams(query) {
      this.tableQueryParams = query
      this.loadMoreCounter = 0 // reset
      await this.getEvents(this.tableQueryParams, 1, 10)
      if (!this.triggerInfiniteScrolling) {
        // window.addEventListener('scroll', this.handleScroll)
        this.triggerInfiniteScrolling = true
      }
    },
    async handleScroll() {
      const scrollTop = window.pageYOffset // document.body.scrollTop;
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.clientHeight
      const winHeight = Math.min(windowHeight, documentHeight)
      const pageHeight = document.documentElement.offsetHeight
      if ((scrollTop > (pageHeight - winHeight) * 0.6) && this.loadMoreCounter < 3) {
        if (this.pagination.current_page < this.pagination.last_page) {
          this.debouncedLoadMore()
        }
      }
    },
    manualLoadMore() {
      this.loadMoreCounter = 0
      this.debouncedLoadMore()
    },
    debouncedLoadMore() {
      if (this.pagination.current_page < this.pagination.last_page) {
        if (this.loadMoreDebounceTimer) {
          clearTimeout(this.loadMoreDebounceTimer)
          // this.loadMoreDebounceTimer = 0
        }
        this.loadMoreDebounceTimer = setTimeout(() => {
          this.loadMore()
          this.loadMoreDebounceTimer = 0
        }, 200)
      }
    },
    async loadMore() {
      if (this.pagination.current_page < this.pagination.last_page) {
        this.isLoadMore = true
        let res = await EventService.getEventList(this.tableQueryParams, this.pagination.current_page + 1, 10, 'list')
        const totalCount = res.meta && (res.meta.current_page * res.meta.per_page)
        if (res && res.data && totalCount > this.tableData.length) {
          this.tableData = this.tableData.concat(res.data)
          this.pagination = {
            current_page: res.meta.current_page,
            last_page: res.meta.last_page,
            per_page: res.meta.per_page
          }
        }
        this.loadMoreCounter = this.loadMoreCounter + 1
        this.isLoadMore = false
      }
      // else no more page
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';
.mk-container{
  &.mk-concert{
    max-width: none;
    padding: 0;
  }
}
.mk-concert{
  margin-bottom: 40px;
}
.container--loader {
  display: block;
  margin: 0 auto;
  text-align: center;
}
.structure-header {
  width: 0;
  height: 0;
  margin: 0;
  padding: 0;
  visibility: hidden;
}
.pgnnp-footer {
  margin: 40px auto;
}
</style>
